import { MutableRefObject, useLayoutEffect, useState } from 'react';

import { getListScrollMargin } from '../model';

/**
 * Offset of the list inside its scroll parent. Measured in a layout effect and
 * on ancestor resizes (content above the list changing height); state updates
 * only when the offset actually moves, so list growth does not re-render.
 */
export function useListScrollMargin(
  listRef: MutableRefObject<HTMLDivElement | null>,
  scrollParent: HTMLElement | null
): number {
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !scrollParent) {
      return;
    }

    const updateMargin = (): void => {
      const next = getListScrollMargin(list, scrollParent);
      setScrollMargin((current) => (current === next ? current : next));
    };

    updateMargin();

    const resizeObserver = new ResizeObserver(updateMargin);
    let ancestor: HTMLElement | null = list.parentElement;
    while (ancestor && ancestor !== scrollParent) {
      resizeObserver.observe(ancestor);
      ancestor = ancestor.parentElement;
    }
    resizeObserver.observe(scrollParent);

    return () => {
      resizeObserver.disconnect();
    };
  }, [listRef, scrollParent]);

  return scrollMargin;
}
